import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { StoreHeader } from '../components/StoreHeader';
import { StoreFooter } from '../components/StoreFooter';
import { useCartStore } from '../stores/cartStore';
import { toast } from 'sonner';
import { useEffect } from 'react';

export default function CartPage() {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart } = useCartStore();

  /* ----------------------------------------
     Effects
  ---------------------------------------- */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  /* ----------------------------------------
     Totals
  ---------------------------------------- */
  const originalTotal = items.reduce(
    (sum, item) =>
      sum + ((item.original_price ?? item.price) * item.quantity),
    0
  );

  const discountedTotal = items.reduce(
    (sum, item) => sum + (item.price * item.quantity),
    0
  );

  const savings = Math.max(0, originalTotal - discountedTotal);
  const savingsPercent =
    originalTotal > 0 ? (savings / originalTotal) * 100 : 0;

  const shipping = discountedTotal > 15000 ? 0 : 300;
  const finalTotal = discountedTotal + shipping;

  /* ----------------------------------------
     Handlers
  ---------------------------------------- */
  const handleRemove = (item) => {
    removeItem(item.id);
    toast.success(`${item.name} removed from cart`);
  };

  const handleDecreaseQty = (item) => {
    if (item.quantity === 1) {
      handleRemove(item);
    } else {
      updateQuantity(item.id, item.quantity - 1);
    }
  };

  const handleIncreaseQty = (item) => {
    updateQuantity(item.id, item.quantity + 1);
  };

  /* ----------------------------------------
     Empty Cart View
  ---------------------------------------- */
  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-stone-100">
        <StoreHeader />

        <main className="max-w-7xl mx-auto px-4 py-16">
          <div className="bg-white border border-stone-200 p-12 text-center">
            <ShoppingBag className="h-16 w-16 text-stone-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-stone-900 mb-2">
              Your cart is empty
            </h2>
            <p className="text-stone-500 mb-6">
              Looks like you haven't added any items yet.
            </p>

            <Link to="/products">
              <Button className="bg-emerald-900 hover:bg-emerald-800">
                Continue Shopping
              </Button>
            </Link>
          </div>
        </main>

        <StoreFooter />
      </div>
    );
  }

  /* ----------------------------------------
     Cart View
  ---------------------------------------- */
  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-stone-900 mb-8">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4 px-2 sm:px-0">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-white border border-stone-200 p-4 flex gap-4 mx-auto w-full max-w-sm sm:max-w-none"
              >
                {/* Image */}
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-stone-100 flex-shrink-0">
                  <img
                    src={item.image || 'https://via.placeholder.com/100'}
                    alt={item.name}
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/products/${item.id}`}
                    className="font-semibold text-stone-900 hover:text-emerald-900 line-clamp-2"
                  >
                    {item.name}
                  </Link>

                  <div className="flex gap-2 mt-2 flex-wrap">
                    {item.size && (
                      <span className="bg-stone-100 border border-stone-300 text-xs px-2 py-1 rounded">
                        Size: {item.size}
                      </span>
                    )}
                    {item.color && (
                      <span className="bg-stone-100 border border-stone-300 text-xs px-2 py-1 rounded">
                        Color: {item.color}
                      </span>
                    )}
                  </div>

                  <p className="text-emerald-900 font-bold mt-1 text-sm">
                    KES {item.price.toLocaleString()}
                  </p>

                  {item.original_price && item.original_price > item.price && (
                    <p className="text-xs text-stone-500 line-through">
                      KES {item.original_price.toLocaleString()}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-stone-200">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDecreaseQty(item)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>

                      <span className="w-8 text-center text-sm">
                        {item.quantity}
                      </span>

                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleIncreaseQty(item)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleRemove(item)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>

                {/* Item Total */}
                <div className="text-right">
                  <p className="font-bold text-sm sm:text-base">
                    KES {(item.price * item.quantity).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}

            <Button
              variant="outline"
              className="mt-4"
              onClick={() => {
                clearCart();
                toast.success('Cart cleared');
              }}
            >
              Clear Cart
            </Button>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-stone-200 p-6 lg:sticky lg:top-24">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Original Price</span>
                  <span>KES {originalTotal.toLocaleString()}</span>
                </div>

                <div className="flex justify-between">
                  <span>You Saved</span>
                  <span className="text-green-600">
                    KES {savings.toLocaleString()} ({savingsPercent.toFixed(1)}%)
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>
                    {shipping === 0 ? 'FREE' : `KES ${shipping.toLocaleString()}`}
                  </span>
                </div>

                <div className="border-t pt-3 mt-3 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-emerald-900">
                    KES {finalTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              <Button
                size="lg"
                className="w-full mt-6 bg-emerald-900 hover:bg-emerald-800"
                onClick={() => navigate('/checkout')}
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <Link
                to="/products"
                className="block text-center mt-4 text-sm text-emerald-900 hover:underline"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </main>

      <StoreFooter />
    </div>
  );
}
